"use client"
import React, { useState, useEffect } from "react";

import "@/components/events/CommunityCalendarView.css";

import useApi from "@/components/api/useApi";
import { Container, Row, Col, Dropdown, Button } from "react-bootstrap";
import { CalendarWidget } from "@/components/shared/calendar/CalendarWidget";
import { useTenant } from "@/lib/providers/TenantProvider";

var moment = require("moment");

const CommunityCalendarView = ({ name, external }) => {
  // const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });

  const { profile, community } = useTenant();
  const [loaded, setLoaded] = useState(false);
  const [startDate, setStartDate] = useState(moment());
  const [eventList, setEventList] = useState([]);
  const [page, setPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(0);
  const [filteredEvents, setFilteredEvents] = useState(null);

  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const [firstLoadTime, setFirstLoadTime] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  const [locationFilter, setLocationFilter] = useState(null);

  const api = useApi();
  const max = 20;

  useEffect(() => {
    if (name && profile && startDate) {
      setPage(0);
      setCurrentPageSize(0);
      setEventList([]);
      queryForEvents(name, [], 0);
    }
  }, [name, profile, startDate]);

  useEffect(() => {
    if (!eventList) {
      setFilteredEvents(null);
      return;
    }

    let filtered = [...eventList];

    if (locationFilter && locationFilter.id) {
      filtered = filtered.filter(
        evt => evt.locationProfile?.userProfileId === locationFilter.id
      );
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(
        evt =>
          Array.isArray(evt.tags) &&
          evt.tags.some(tag => selectedTags.includes(tag))
      );
    }

    if (selectedDate) {
      filtered = filtered.filter(evt =>
        evt.startDate ? sameDay(moment(evt.startDate), selectedDate) : false
      );
    }

    const filtersActive =
      (locationFilter && locationFilter.id) ||
      selectedTags.length > 0 ||
      !!selectedDate;

    setFilteredEvents(filtersActive ? filtered : null);
  }, [eventList, locationFilter, selectedTags, selectedDate]);

  useEffect(() => {
    if (!eventList || eventList.length === 0) {
      if (availableTags.length > 0) {
        setAvailableTags([]);
      }
      if (locationOptions.length > 0) {
        setLocationOptions([]);
      }
      if (selectedTags.length > 0) {
        setSelectedTags([]);
      }
      if (locationFilter !== null) {
        setLocationFilter(null);
      }
      return;
    }

    const tagsSet = new Set();
    const locationList = [];

    eventList.forEach(evt => {
      if (Array.isArray(evt.tags)) {
        evt.tags.filter(Boolean).forEach(tag => {
          tagsSet.add(tag);
        });
      }

      if (
        evt.locationProfile &&
        evt.locationProfile.locationSource === "URALITY_PROFILE" &&
        evt.locationProfile.userProfileId
      ) {
        if (
          locationList.some(loc => loc.id === evt.locationProfile.userProfileId)
        ) {
          return;
        }
        locationList.push({
          id: evt.locationProfile.userProfileId,
          label: evt.location
        });
      }
    });

    const tags = Array.from(tagsSet).sort((a, b) => a.localeCompare(b));
    setAvailableTags(prev => {
      if (
        prev.length === tags.length &&
        prev.every((tag, idx) => tag === tags[idx])
      ) {
        return prev;
      }
      return tags;
    });

    setLocationOptions(
      locationList.sort((a, b) => a.label.localeCompare(b.label))
    );

    if (selectedTags.length > 0) {
      const cleanedTags = selectedTags.filter(tag => tagsSet.has(tag));
      if (cleanedTags.length !== selectedTags.length) {
        setSelectedTags(cleanedTags);
      }
    }

    if (
      locationFilter &&
      locationFilter.id &&
      !locationList.some(loc => loc.id === locationFilter.id)
    ) {
      setLocationFilter(null);
    }
  }, [eventList, selectedTags, locationFilter]);

  const sameDay = (date, refDate) => {
    return (
      date.year() === refDate.year() &&
      date.month() === refDate.month() &&
      date.date() === refDate.date()
    );
  };

  const queryForEvents = (name, existingEvents = [], pg = 0) => {
    if (profile.userProfileType === "COMMUNITY") {
      const theStartDate = startDate ? startDate : moment();
      const startDateString = theStartDate.format("YYYY-MM-DDT00:00:00");
      const endDate = moment(theStartDate)
        .endOf("month")
        .add(1, "days");
      const endDateString = endDate.format("YYYY-MM-DDT00:00:00");

      const payload = {
        queryType: "EVENT",
        page: pg,
        max: max,
        startDate: startDateString,
        endDate: endDateString
      };

      api
        .queryProfileCommunity(name, payload)
        .then(result => {
          if (result.events.length > 0) {
            setCurrentPageSize(result.events.length);
            const previousEvents = [...existingEvents];

            const eventsForCalendar = result.events.map(evt => ({
              id: evt.id,
              title: evt.title,
              subtitle: evt.subtitle,
              tags: evt.tags,
              location: evt.location?.name,
              locationProfile: evt.location,
              multiDay: evt.multiDay,
              date: evt.startDate?.date,
              startDate: evt.startDate?.date,
              endDate: evt.endDate?.date,
              description: evt.description,
              callToActions: evt.ctas,
              coverPhoto: evt.coverPhotoAsset?.id
            }));

            const updatedEventList = previousEvents.concat(eventsForCalendar);

            setEventList(updatedEventList);
            setFilteredEvents(null);

            setLoaded(true);
            setFirstLoadTime(false);
          } else if (result.nextEventDate && firstLoadTime) {
            // check to see if nextEventDate is set on the result, and if it is, put it on the
            // query and try again
            setFirstLoadTime(false);
            setStartDate(moment(result.nextEventDate)); // this will trigger another
          } else {
            setCurrentPageSize(0);
            const previousEvents = [...existingEvents];
            const eventsForCalendar = result.events.map(evt => ({
              id: evt.id,
              title: evt.title,
              date: evt.startDate?.date,
              location: evt.location?.name,
              locationProfile: evt.location,
              multiDay: evt.multiDay,
              description: evt.description,
              // callToAction: { label: "Book Now", url: evt.bookingLinkUrl },
              coverPhoto: evt.coverPhotoAsset?.id
            }));

            const updatedEventList = previousEvents.concat(eventsForCalendar);

            setEventList(updatedEventList);
            setFilteredEvents(null);
            setLoaded(true);
          }
        })
        .catch(error => {
          // console.log(error);
          setLoaded(true);
          setFirstLoadTime(false);
        });
    } else {
      api
        .listProfileEvents(name)
        .then(eventList => {
          const eventsForCalendar = eventList.map(evt => ({
            id: evt.id,
            title: evt.title,
            subtitle: evt.subtitle,
            tags: evt.tags,
            location: evt.location?.name,
            locationProfile: evt.location,
            multiDay: evt.multiDay,
            date: evt.startDate?.date,
            startDate: evt.startDate?.date,
            endDate: evt.endDate?.date,
            description: evt.description,
            callToActions: evt.ctas,
            coverPhoto: evt.coverPhotoAsset?.id
          }));

          setEventList(eventsForCalendar);
          setFilteredEvents(null);
          setLoaded(true);
        })
        .catch(error => {
          // console.log(error);
          setLoaded(true);
        });
    }
  };

  const handleLocationSelect = option => {
    setLocationFilter(option);
  };

  const handleTagToggle = tag => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      }
      return [...prev, tag];
    });
  };

  const handleClearFilters = () => {
    setLocationFilter(null);
    setSelectedTags([]);
    setSelectedDate(null);
  };

  const dateChanged = newDate => {
    setSelectedDate(null);
    setStartDate(newDate);
  };

  if (!loaded) {
    return <></>;
  } else {
    const theEvents = filteredEvents ?? eventList;
    const selectedLocationLabel = locationFilter?.label ?? "All Locations";
    const filtersActive =
      (locationFilter && locationFilter.id) ||
      selectedTags.length > 0 ||
      !!selectedDate;
    const tagFilterLabel =
      selectedTags.length > 0 ? `Tags (${selectedTags.length})` : "Tags";

    return (
      <div className={`community-calendar-view ${external ? "embedded" : ""}`}>
        <Container>
          <Row>
            <Col xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="calendar-filter-bar">
                <div className="calendar-filter-label">
                  <i className="fa-regular fa-filter"></i>&nbsp;Filters:
                </div>
                <Dropdown>
                  <Dropdown.Toggle
                    variant="secondary"
                    className="btn btn-sm btn-secondary"
                    id="community-calendar-location-filter"
                  >
                    {selectedLocationLabel}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      active={!locationFilter}
                      onClick={() => handleLocationSelect(null)}
                    >
                      All Locations
                    </Dropdown.Item>
                    {locationOptions &&
                      locationOptions.length > 0 &&
                      locationOptions.map(option => (
                        <Dropdown.Item
                          key={option.id}
                          active={locationFilter?.id === option.id}
                          onClick={() => handleLocationSelect(option)}
                        >
                          {option.label}
                        </Dropdown.Item>
                      ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown autoClose="outside">
                  <Dropdown.Toggle
                    variant="secondary"
                    className="btn btn-sm btn-secondary"
                    id="community-calendar-tag-filter"
                  >
                    {tagFilterLabel}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="tag-dropdown-menu">
                    {availableTags.length > 0 ? (
                      <div className="tag-dropdown-options">
                        {availableTags.map(tag => {
                          const isSelected = selectedTags.includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              className={`tag-option-button ${
                                isSelected ? "selected" : ""
                              }`}
                              onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleTagToggle(tag);
                              }}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="tag-dropdown-empty">
                        No tags available
                      </div>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                <button
                  type="button"
                  className={`a clear-filters-button ${
                    filtersActive ? "active" : "hidden"
                  }`}
                  onClick={handleClearFilters}
                >
                  Clear filters
                </button>
              </div>
              {selectedTags.length > 0 && (
                <div className="selected-tags-bar">
                  {selectedTags.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      className="selected-tag-pill"
                      onClick={() => handleTagToggle(tag)}
                    >
                      {tag}
                      <span className="selected-tag-pill-remove">&times;</span>
                    </button>
                  ))}
                </div>
              )}
              <CalendarWidget events={theEvents} />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
};

export default CommunityCalendarView;
